<template>
  <application-layout>
    <v-main>
      <v-container>
        <h1>Manage Facility Features</h1>
        <p>
          Below is a list of facility features that can be assigned to facilities. To actually assign a
          feature to a facility, navigate to the Manage Facilities section, select the facility, and then
          assign features via the 'Facility Features' tab.
        </p>

        <v-dialog
          v-model="addFeature"
          persistent
          max-width="600"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              data-testid="feature-index-modal-add-feature-btn"
              class="feature-index-modal-add-feature-btn mb-4 ml-0"
              v-on="on"
              color="primary"
            >
              <v-icon>add</v-icon>
              Add New Feature
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="headline">Add New Feature</v-card-title>
            <v-divider />
            <v-form
              ref="createFeatureForm"
              @submit.prevent="submitFeatureForm"
            >
              <v-card-text>
                <v-select
                  data-testid="feature-index-category-select"
                  class="feature-index-category-select"
                  label="Category"
                  :items="featureCategories"
                  v-model="newFeatureData.category"
                  clearable
                  filled
                />
                <app-input
                  data-testid="feature-index-text-input"
                  class="feature-index-text-input"
                  :type="'text'"
                  label="Feature Text"
                  v-model.trim="newFeatureData.text"
                  :rules="{ required: true }"
                  :useValidation="true"
                />
                <v-select
                  data-testid="feature-index-icon-select"
                  class="feature-index-icon-select"
                  :items="availableIcons"
                  label="Icon"
                  v-model="newFeatureData.icon"
                  clearable
                  filled
                >
                  <template slot="selection" slot-scope="data">
                    <v-icon>{{ data.item }}</v-icon>
                  </template>

                  <template slot="item" slot-scope="data">
                    <v-icon>{{ data.item }}</v-icon>&nbsp;&nbsp;&nbsp;{{ data.item }}
                  </template>
                </v-select>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  data-testid="feature-index-cancel-btn"
                  class="feature-index-cancel-btn"
                  color="error"
                  @click="addFeature = false"
                >
                  Cancel
                </v-btn>
                <v-spacer />
                <v-btn
                  data-testid="feature-index-add-btn"
                  class="feature-index-add-btn"
                  color="primary"
                  type="submit"
                  :loading="loadingState.includes('creating feature')"
                  :disabled="!validFields"
                >
                  Add Feature
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>

        <v-data-table
          :footer-props="{
            ...footerProps,
            itemsPerPageOptions: pagination.rowsPerPageOptions
          }"
          :headers="headers"
          :items="features"
          :loading="tableLoading"
          :options.sync="pagination"
          :server-items-length="pagination.totalItems"
        >
          <template v-slot:body="{ items }">
            <transition-group
              name="scroll-y-transition"
              tag="tbody"
              v-if="items.length"
            >
              <tr v-for="item in items" :key="item.id">
                <td>{{ item.category }}</td>
                <td>{{ item.text }}</td>
                <td>
                  <v-icon>{{ item.icon }}</v-icon>
                </td>
                <td>
                  <v-btn
                    data-testid="feature-index-edit-btn"
                    class="feature-index-edit-btn"
                    small
                    color="primary"
                    :to="{ name: 'facilityFeaturesEdit', params: { featureId: item.id } }"
                  >
                    Edit
                  </v-btn>
                  <v-btn
                    data-testid="feature-index-delete-btn"
                    class="feature-index-delete-btn ml-3 error"
                    small
                    @click="openDelete(item)"
                    :loading="loadingState.includes('deleting feature')"
                  >
                    Delete
                  </v-btn>
                </td>
              </tr>
            </transition-group>
            <tr v-else>
              <td colspan="12" style="text-align: center">
                No features found.
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-container>
    </v-main>

    <delete-modal
      v-model="modal"
      @delete="deleteFeature"
      :itemType="'feature'"
      :advisory="genericAdvisoryText"
    />
  </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { facilityFeatureHeaders } from "@/helpers/facility/facility-features-data-table-headers";
import { footerProps } from "@/helpers/footer-props";
import { genericAdvisory } from "@/helpers/warning-advisory-text";

export default {
  data() {
    return {
      tableLoading: true,
      genericAdvisoryText: genericAdvisory,
      headers: facilityFeatureHeaders,
      footerProps: footerProps,
      addFeature: false,
      newFeatureData: {
        category: null,
        text: null,
        icon: null
      },
      modal: false,
      selectedFeature: ""
    };
  },

  methods: {
    async submitFeatureForm() {
      await this.$store.dispatch("createFeature", this.newFeatureData)
        .then(() => {
          this.newFeatureData = {
            category: null,
            text: null,
            icon: null
          };
          this.addFeature = false;
        });
    },

    openDelete(feature) {
      this.modal = true;
      this.selectedFeature = feature;
    },

    deleteFeature() {
      this.$store.dispatch("deleteFeature", this.selectedFeature.id);
      this.selectedFeature = "";
      this.modal = false;
    }
  },

  computed: {
    availableIcons() {
      return this.$store.state.facilityFeature.availableIcons;
    },
    features() {
      return this.$store.state.facilityFeature.features;
    },
    featureCategories() {
      return this.$store.state.facilityFeature.featureCategories;
    },
    loadingState() {
      return this.$store.state.facilityFeature.loadingState;
    },
    pagination: {
      get() {
        return this.$store.state.facilityFeature.pagination;
      },
      set(newValue) {
        this.$store.commit("setFeaturePagination", newValue);
      }
    },
    validFields() {
      return this.newFeatureData.category && this.newFeatureData.icon && this.newFeatureData.text;
    }
  },

  watch: {
    pagination: {
      handler() {
        this.tableLoading = true;
        this.$store.dispatch("getAllFeatures")
          .then(() => {
            this.tableLoading = false;
          });
      },
      deep: true
    }
  },

  components: {
    ApplicationLayout,
    DeleteModal
  }
};
</script>
